'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import Link from 'next/link';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import type { MktClickParams, ProductListItem, ProductListProps } from '../types';

// 单个产品卡片组件
function ProductCard({
  product,
  index,
  isWeb,
  hotImg,
  onMktExp,
  onMktClick,
}: {
  product: ProductListItem;
  index: number;
  isWeb: boolean;
  hotImg: string;
  onMktExp: (params: any) => void;
  onMktClick: (mktName: string, params: MktClickParams) => void;
}) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();
  const [productRef, productInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理产品点击
  const handleProductClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // 与 Vue 对齐：点击跳转商品详情
    onMktClick(`Product_${index}`, {
      shop_sku: `${product.shop_sku}`,
      business_sku: `${product.business_sku}`,
      product_sku: `${product.sku}`,
      group_id: ``,
    });
    navigate(`/goods-detail/${product.shop_sku}`);
  };

  // 显示 Hot 标签
  const showHotTag = product.has_hot === '1' && hotImg;
  // 显示广告图
  const showAdvertisingImg = product.advertising_img;

  // 曝光埋点（与 Vue：Product_{index} 对齐）
  useEffect(() => {
    if (productInView) {
      onMktExp({
        mkt_name: `Product_${index}`,
        shop_sku: (product as any).shop_sku || '',
        business_sku: (product as any).business_sku || '',
        product_sku: (product as any).sku || '',
        group_id: '',
      });
    }
  }, [productInView, index, product, onMktExp]);

  // 价格信息
  const pInfo: any = priceInfo(userInfo, product as any, currency) || {};
  const nowPriceText = formatPriceNumberWithSymbol(
    String(pInfo?.sell_price || pInfo?.price || '0'),
    currency,
  );
  const oldPriceText =
    pInfo && pInfo.price && pInfo.sell_price && pInfo.sell_price !== pInfo.price
      ? formatPriceNumberWithSymbol(String(pInfo.price), currency)
      : '';

  // 图片选择
  const productImg: string = (() => {
    const imgList: string[] = (product as any)?.image?.product || [];
    if (product.advertising_img) return product.advertising_img as string;
    if (Array.isArray(imgList) && imgList.length > 0) return imgList[0];
    return '';
  })();

  // PC 端悬浮图与名人图判断（仅 PC 使用）
  const productShowImg = (() => {
    const celebrity: string[] = (product as any)?.image?.celebrity || [];
    const imgList: string[] = (product as any)?.image?.product || [];
    if (product.advertising_img) return '';
    if (celebrity.length) return celebrity[0];
    if (imgList.length > 1) return imgList[1];
    return '';
  })();
  const isShowCelebrity = (() => {
    const celebrity: string[] = (product as any)?.image?.celebrity || [];
    return celebrity.length > 0;
  })();

  const isFullWidth = (index + 1) % 3 === 0;
  const addRightPad = index % 3 === 0; // 第1、4、7...
  const isPc = isWeb;
  const isPcSpecialW2_3 = isPc && index === 4; // 第5个占 2/3 宽
  let baseWidthClass = '';
  if (isPc) {
    baseWidthClass = isPcSpecialW2_3 ? 'w-2/3' : 'w-1/3';
  } else {
    baseWidthClass = isFullWidth ? 'w-full' : 'w-1/2';
  }

  let prClass = '';
  if (isPc) {
    prClass = index === 2 || index === 4 || index === 7 ? '' : 'pr-[1px]';
  } else {
    prClass = addRightPad ? 'pr-[1px]' : '';
  }
  const radiusClass = isPc ? 'rounded-[20px]' : 'rounded-[10px]';

  // 预计算样式，避免 JSX 中多层三元
  const imgWrapStyle = isPc ? { height: '32.76vw' } : { height: '175px' };
  const productImgClass = 'product-img block w-[90%] mx-auto';
  const productImgAdClass = 'product-img big-product-img w-full h-full object-cover';
  const placeholderTextClass = isPc ? 'text-[16px]' : 'text-[14px]';
  const hotStyle = isPc
    ? { width: '3.28vw', top: '1.25vw', right: '1.15vw' }
    : { width: '27px', top: '7px', right: '7px' };
  const infoStyle = isPc
    ? { height: '5vw', padding: '0 1.04vw 0 2.34vw' }
    : { height: '53px', paddingLeft: '8px', paddingRight: '6px' };
  const nowPriceStyle = { fontSize: isPc ? '1.25vw' : '13px' };
  const oldPriceStyle = {
    paddingLeft: isPc ? '0.52vw' : '',
    fontSize: isPc ? '1.04vw' : '10px',
    color: '#999',
  };
  const rightBtnStyle = {
    width: isPc ? '11.35vw' : '88px',
    height: isPc ? '3.125vw' : '28px',
    fontSize: isPc ? '1.25vw' : '12px',
  };

  // 构建媒体节点，避免 JSX 内嵌套三元
  let mediaNode: React.ReactNode;
  if (showAdvertisingImg) {
    mediaNode = (
      <Image
        src={product.advertising_img!}
        alt={`Product ${product.shop_sku}`}
        fill
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        className={productImgAdClass}
      />
    );
  } else if (productImg) {
    mediaNode = (
      <Image
        src={productImg}
        alt={String((product as any).name || product.shop_sku)}
        className={productImgClass}
        width={isWeb ? 1000 : 375}
        height={isWeb ? 1000 : 375}
      />
    );
  } else {
    mediaNode = (
      <div
        className={`w-full h-full bg-[#f0f0f0] flex items-center justify-center text-[#999] ${placeholderTextClass}`}
      >
        {product.shop_sku}
      </div>
    );
  }

  return (
    <Link
      ref={productRef as any}
      href={`/goods-detail/${product.shop_sku}`}
      className={`group product-item block ${baseWidthClass} ${prClass} shadow-[0_0_0_1px_#000] ${radiusClass} box-border overflow-hidden mb-[1px]`}
      onClick={handleProductClick}
    >
      {/* 图片区域 */}
      <div
        className="img-wrap relative flex items-center justify-center bg-white"
        style={imgWrapStyle}
      >
        {/* 主要显示内容 - 广告图优先 */}
        {mediaNode}

        {/* PC 悬浮展示图 */}
        {isPc && productShowImg && (
          <div className="hover-img-box absolute inset-0 z-[1] w-full h-full opacity-0 flex items-center justify-center overflow-hidden transition-opacity duration-300 ease-out group-hover:opacity-100">
            <Image
              src={productShowImg}
              alt={`${(product as any).name || product.shop_sku} hover`}
              className={`${isShowCelebrity ? 'w-full' : 'w-[90%]'} h-auto object-contain`}
              fill
            />
          </div>
        )}

        {/* Hot 标签 */}
        {showHotTag && (
          <Image
            src={hotImg}
            alt="Hot"
            className="hot-img h-auto absolute z-[2]"
            width={30}
            height={30}
            style={hotStyle}
          />
        )}

        {/* 非广告图时显示名称（仅移动端覆盖显示；PC 放到下方信息区） */}
        {!isPc && !showAdvertisingImg && (
          <p className="name absolute bottom-4 left-0 w-full text-center text-[12px]">
            {(product as any).short_name || (product as any).name || product.shop_sku}
          </p>
        )}
      </div>

      {/* 产品信息 */}
      <div
        className="product-info flex justify-between items-center border-t border-black box-border bg-white"
        style={infoStyle}
      >
        <div className="left">
          {isPc && (
            <p className="name" style={{ marginBottom: '0.42vw', fontSize: '1.25vw' }}>
              {(product as any).short_name || (product as any).name || product.shop_sku}
            </p>
          )}
          <div className={`price notranslate flex items-baseline ${isPc ? '' : 'flex-col'}`}>
            <span className="now-price notranslate font-bold" style={nowPriceStyle}>
              {nowPriceText}
            </span>
            {oldPriceText && (
              <span className="old-price notranslate line-through" style={oldPriceStyle}>
                {oldPriceText}
              </span>
            )}
          </div>
        </div>
        <div
          className="right bg-primary-black text-white font-bb text-center flex items-center justify-center"
          style={rightBtnStyle}
        >
          SHOP NOW
        </div>
      </div>
    </Link>
  );
}

export default function ProductList({
  productLists,
  hotImg,
  isWeb,
  onMktExp,
  onMktClick,
}: ProductListProps) {
  if (!productLists || productLists.length === 0) {
    return null;
  }

  return (
    <div className="denim-goods-list flex flex-wrap bg-[#a1bcd2] p-[1px]">
      {productLists.map((product, index) => (
        <ProductCard
          key={product.id}
          product={product}
          index={index}
          isWeb={isWeb}
          hotImg={hotImg}
          onMktClick={onMktClick}
          onMktExp={onMktExp}
        />
      ))}
    </div>
  );
}
